import { useState, useRef, useCallback, useEffect } from "react";
import { LuSearch } from "react-icons/lu";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import * as Portal from "@radix-ui/react-portal";
import { searchSort } from "@/utils/searchSort";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { RiAddLine } from "@remixicon/react";
import { ColumnOption as Metric } from "@/types";
import {
  SqTooltip,
  SqTooltipContent,
  SqTooltipProvider,
  SqTooltipTrigger,
} from "@/components/squircle-ui/tooltip";
import { getPlatformIcon, sortMetricItems } from "@/screens/utils/Helpers";

import { IconDatabase } from "@tabler/icons-react";

interface MetricsDropdownProps {
  metricsOptions: Metric[];
  selectedMetrics: Metric[];
  onMetricsChange: (metrics: Metric[]) => void;
  className?: string;
  disabled?: boolean;
  trigger?: React.ReactNode;
  contentClassName?: string;
  collisionPadding?: number;
  sideOffset?: number;
  // Single select mode props
  singleSelect?: boolean;
  defaultMetric?: string;
  onSetDefault?: (value: string) => void;
  // Color function to get color for a metric
  getMetricColor?: (metric: Metric, index: number) => string;
  // Function to determine if a metric should be disabled
  isMetricDisabled?: (metric: Metric) => boolean;
  // Sort metrics alphabetically
  sortAlphabetically?: boolean;
}

const AddMetricTrigger = (
  <Button className="shrink-0 border-0 h-7 gap-1 px-3 py-1 relative !squircle !squircle-smooth-[1] !squircle-radius-[6px] !squircle-fill-[white] before:absolute before:inset-0 before:squircle before:squircle-radius-[6px] before:squircle-smooth-[1] before:squircle-outline-[1px] before:squircle-outline-color-[#E7E8E9] before:pointer-events-none">
    <RiAddLine size={14} color="#FF4500" className="shrink-0" />
    <span className="text-[#FF4500] font-normal not-italic text-[12px] leading-[19.16px] tracking-[0.02em] translate-y-[1px]">
      Add Metric
    </span>
  </Button>
);

export function MetricsDropdown({
  metricsOptions,
  selectedMetrics = [],
  onMetricsChange,
  className,
  disabled = false,
  trigger = AddMetricTrigger,
  contentClassName = "",
  collisionPadding = 10,
  sideOffset = 5,
  singleSelect = false,
  defaultMetric,
  onSetDefault,
  getMetricColor,
  isMetricDisabled,
  sortAlphabetically = true,
}: MetricsDropdownProps) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const triggerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);

  const handleOpenChange = useCallback(
    (isOpen: boolean) => {
      if (isOpen && triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect();
        const dropdownWidth = 185; // Default width
        const dropdownHeight = 300; // Max height

        let top = rect.bottom + sideOffset;
        // Align right edge of dropdown with right edge of trigger
        let left = rect.right - dropdownWidth;
        // Check left edge collision
        if (left < collisionPadding) {
          left = collisionPadding;
        }

        // Check right edge collision
        if (left + dropdownWidth + collisionPadding > window.innerWidth) {
          left = window.innerWidth - dropdownWidth - collisionPadding - 16; // Add 16px margin from right edge
        }

        // Check bottom edge collision
        if (top + dropdownHeight + collisionPadding > window.innerHeight) {
          top = rect.top - dropdownHeight - sideOffset;
        }

        // Check top edge collision
        if (top < collisionPadding) {
          top = collisionPadding;
        }
        setDropdownPosition({ top, left });
      } else {
        setDropdownPosition(null);
        setSearchQuery("");
      }
      setOpen(isOpen);
    },
    [collisionPadding, sideOffset],
  );

  // Handle click outside and scroll prevention
  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (event: MouseEvent | FocusEvent) => {
      const target = event.target as Node;
      if (
        triggerRef.current &&
        !triggerRef.current.contains(target) &&
        contentRef.current &&
        !contentRef.current.contains(target)
      ) {
        handleOpenChange(false);
      }
    };

    // Prevent body/html scroll when dropdown is open
    const htmlEl = document.documentElement;
    const prevHtmlOverflow = htmlEl.style.overflow;
    const prevBodyOverflow = document.body.style.overflow;

    const isEventInsideDropdown = (target: Node) => {
      return contentRef.current?.contains(target) || false;
    };

    const preventScrollOutside: EventListener = (e) => {
      const target = e.target as Node;
      if (!isEventInsideDropdown(target)) {
        e.preventDefault();
      }
    };

    // Apply scroll locks
    htmlEl.style.overflow = "hidden";
    document.body.style.overflow = "hidden";
    window.addEventListener("wheel", preventScrollOutside, {
      passive: false,
      capture: true,
    });
    window.addEventListener("touchmove", preventScrollOutside, {
      passive: false,
      capture: true,
    });

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("focusin", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("focusin", handleClickOutside);

      // Restore scroll and remove listeners
      htmlEl.style.overflow = prevHtmlOverflow;
      document.body.style.overflow = prevBodyOverflow;
      window.removeEventListener("wheel", preventScrollOutside, { capture: true });
      window.removeEventListener("touchmove", preventScrollOutside, { capture: true });
    };
  }, [open, handleOpenChange]);

  // Sort options only when dropdown opens
  const [sortedOptions, setSortedOptions] = useState<Metric[]>(metricsOptions);
  const selectedMetricValues = selectedMetrics.map((m) => m.key);

  // Effect to sort metrics properly
  useEffect(() => {
    if (!open) return;

    if (singleSelect) {
      // Single select mode: Keep default metric at top, then sort
      const sortedMetrics = sortAlphabetically
        ? sortMetricItems([...metricsOptions])
        : [...metricsOptions];
      if (defaultMetric) {
        const defaultIndex = sortedMetrics.findIndex(
          (metric) => metric.key === defaultMetric,
        );
        if (defaultIndex > 0) {
          const defaultMetricItem = sortedMetrics.splice(defaultIndex, 1)[0];
          sortedMetrics.unshift(defaultMetricItem);
        }
      }
      setSortedOptions(sortedMetrics);
    } else {
      // Multi select mode: Selected items first, then sort
      let initialSortedMetrics: Metric[];
      if (sortAlphabetically) {
        initialSortedMetrics = sortMetricItems([...metricsOptions]);
      } else {
        initialSortedMetrics = [...metricsOptions];
      }
      // Keep selected items first
      initialSortedMetrics.sort((a, b) => {
        const aSelected = selectedMetricValues.includes(a.key);
        const bSelected = selectedMetricValues.includes(b.key);
        // Selected items first
        if (aSelected && !bSelected) return -1;
        if (!aSelected && bSelected) return 1;
        return 0;
      });
      setSortedOptions(initialSortedMetrics);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, sortAlphabetically, metricsOptions]);

  // Filter options based on search query, exact/starts-with matches first
  const filteredOptions = searchQuery.trim()
    ? searchSort(
        sortedOptions.filter((option) =>
          option.label.toLowerCase().includes(searchQuery.toLowerCase()),
        ),
        searchQuery,
        (o) => o.label,
      )
    : sortedOptions;

  const handleMetricToggle = (metric: Metric) => {
    // Don't toggle if metric is disabled
    if (isMetricDisabled?.(metric)) {
      return;
    }

    // Store current scroll position
    const scrollTop = scrollContainerRef.current?.scrollTop || 0;

    if (singleSelect) {
      // Single select mode - select only this metric and close dropdown
      onMetricsChange?.([metric]);
      handleOpenChange(false);
    } else {
      // Multi-select mode
      const newSelectedValues = selectedMetricValues.includes(metric.key)
        ? selectedMetrics.filter((m) => m.key !== metric.key)
        : [...selectedMetrics, metric];
      onMetricsChange?.(newSelectedValues);
      // Restore scroll position after state update
      requestAnimationFrame(() => {
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollTop = scrollTop;
        }
      });
    }
  };

  return (
    <div>
      <div
        ref={triggerRef}
        onClick={() => !disabled && handleOpenChange(!open)}
        className={cn(className, open && !singleSelect && "opacity-60")}
      >
        {trigger}
      </div>
      {open && dropdownPosition && (
        <Portal.Root>
          <div
            ref={contentRef}
            className={cn(
              "fixed z-50 p-1 shadow-none border-0 squircle squircle-smooth-[1] squircle-radius-[12px] rounded-[12px] squircle-fill-[white] w-auto drop-shadow-md before:absolute before:inset-0 before:squircle before:squircle-outline-[1px] before:squircle-outline-color-border-gray before:squircle-radius-[12px] before:pointer-events-none",
              "w-[185px]",
              contentClassName,
            )}
            style={{
              top: dropdownPosition.top,
              left: dropdownPosition.left,
            }}
          >
            <div className="mb-[4px]">
              <div className="relative">
                <LuSearch
                  size={14}
                  className="absolute left-2 top-1/2 transform -translate-y-1/2 text-brand-primary-400"
                />
                <Input
                  placeholder="Search metrics..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-7 h-[28px] text-[12px] text-brand-primary-new placeholder:text-brand-primary-400 rounded-[8px] bg-selected-gray flex items-center self-stretch pr-[8px] pt-[8px] pb-[6px] border-none focus:ring-0 shadow-none"
                />
              </div>
            </div>
            <div ref={scrollContainerRef} className="max-h-[284px] overflow-y-auto">
              {filteredOptions.length > 0 ? (
                <SqTooltipProvider delayDuration={200}>
                  {filteredOptions.map((metric) => {
                    const selectedIndex = selectedMetrics.findIndex(
                      (m) => m.key === metric.key,
                    );
                    const metricColor =
                      getMetricColor && selectedIndex >= 0
                        ? getMetricColor(metric, selectedIndex)
                        : undefined;
                    const isDisabled = isMetricDisabled?.(metric) ?? false;
                    return (
                      <MetricDropdownItem
                        key={metric.key}
                        metric={metric}
                        isSelected={selectedMetricValues.includes(metric.key)}
                        handleMetricToggle={handleMetricToggle}
                        singleSelect={singleSelect}
                        isDefault={defaultMetric === metric.key}
                        onSetDefault={onSetDefault}
                        color={metricColor}
                        isDisabled={isDisabled}
                      />
                    );
                  })}
                </SqTooltipProvider>
              ) : (
                <div className="px-[8px] py-[12px] text-center text-brand-primary-400 text-[11px]">
                  No results found
                </div>
              )}
            </div>
          </div>
        </Portal.Root>
      )}
    </div>
  );
}

const MetricDropdownItem = ({
  metric,
  handleMetricToggle,
  isSelected,
  singleSelect,
  isDefault,
  onSetDefault,
  color,
  isDisabled = false,
}: {
  metric: Metric;
  handleMetricToggle: (metric: Metric) => void;
  isSelected: boolean;
  singleSelect?: boolean;
  isDefault?: boolean;
  onSetDefault?: (metricKey: string) => void;
  color?: string;
  isDisabled?: boolean;
}) => {
  return (
    <SqTooltip key={metric.key}>
      <SqTooltipTrigger asChild>
        <div
          onClick={() => !isDisabled && handleMetricToggle(metric)}
          className={`group w-full px-[8px] py-[4px] rounded-[8px] flex gap-2 ${
            isDisabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"
          } ${singleSelect && isSelected ? "bg-selected-gray" : "hover:bg-hover-gray"}`}
        >
          <div className="flex gap-2 flex-1">
            {!singleSelect && (
              <Checkbox
                checked={isSelected}
                disabled={isDisabled}
                className={cn("h-[14px] w-[14px] rounded-[4px] translate-y-[3px]")}
                style={
                  isSelected && color
                    ? ({
                        // Override squircle fill color using CSS custom property
                        // This will override the default brand-orange from the checkbox component
                        "--squircle-fill": color,
                      } as React.CSSProperties)
                    : undefined
                }
              />
            )}

            {metric?.show_platforms &&
              metric?.platforms &&
              metric?.platforms.length > 0 && (
                <div className="flex items-center gap-1 self-start translate-y-[3px]">
                  {metric.platforms.length <= 2 ? (
                    // Show all icons if 2 or fewer platforms
                    metric.platforms?.map((p, index) => (
                      <img
                        key={index}
                        className="h-[12px] w-[12px] flex-shrink-0"
                        src={getPlatformIcon(p)}
                      />
                    ))
                  ) : (
                    // Show first icon + count for remaining
                    <>
                      <img
                        className="h-[14px] w-[14px] flex-shrink-0"
                        src={getPlatformIcon(metric.platforms[0])}
                      />
                      <span className="translate-y-[1px] text-[#02185999]">
                        +{metric.platforms.length - 1}
                      </span>
                    </>
                  )}
                </div>
              )}

            {metric?.sub_type === "EXTERNAL" && (
              <IconDatabase
                size={14}
                className={`text-[#02185999] shrink-0 translate-y-[3px]`}
              />
            )}
            <span className="text-brand-primary-800 text-[12px] font-normal leading-[20px] translate-y-[0.5px] max-w-[150px]">
              {metric.label}
              {metric.show_platforms && <span className="text-[#FF4500]">*</span>}
            </span>
          </div>

          {/* Set Default button for single select mode */}
          {singleSelect && isDefault && (
            <div className="h-full flex self-center items-center justify-center px-1 text-[11px] text-brand-orange font-medium squircle-mask squircle-radius-[6px] squircle-smooth-[1] bg-brand-orange/10">
              <span className="mt-[1px]">Default</span>
            </div>
          )}
          {singleSelect && !isDefault && onSetDefault && (
            <button
              className="h-full self-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 group-hover:duration-1000 flex items-center"
              onClick={(e) => {
                e.stopPropagation();
                onSetDefault?.(metric.key);
              }}
            >
              <span className="mt-[1px] text-[11px] text-brand-primary-600 hover:text-brand-orange whitespace-nowrap">
                Set as Default
              </span>
            </button>
          )}
        </div>
      </SqTooltipTrigger>
      {metric?.show_platforms && metric?.platforms && (
        <SqTooltipContent portal={true} side="top" align="start" alignOffset={80}>
          <p>
            {metric.label}
            <br />
            Available for{" "}
            {metric.platforms
              .map(
                (platform) =>
                  platform.charAt(0).toUpperCase() + platform.slice(1).toLowerCase(),
              )
              .join(", ")}
          </p>
        </SqTooltipContent>
      )}
    </SqTooltip>
  );
};
